import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
// import { ErrorBoundary } from 'react-error-boundary'

// Layout components
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import SkipLink from './components/SkipLink'
import ScrollToTop from './components/ScrollToTop'
import ProtectedRoute from './components/ProtectedRoute'
import BusinessListingForm from './components/BusinessListingForm'
import { AuthProvider } from './contexts/AuthContext'
import { AnonymousProvider } from './contexts/AnonymousContext'
import { ConsentProvider, useConsent } from './contexts/ConsentContext'
import CookieConsent from './components/CookieConsent'
import { analyticsService } from './services/analyticsService'
import { usePageTracking } from './hooks/useAnalytics'

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'))
const CategoryPage = lazy(() => import('./pages/CategoryPage'))
const BusinessListingsPage = lazy(() => import('./pages/BusinessListingsPage'))
const BusinessProfilePage = lazy(() => import('./pages/BusinessProfilePage'))
const EnhancedAdminDashboard = lazy(() => import('./pages/EnhancedAdminDashboard'))
const EnhancedBusinessOwnerDashboard = lazy(() => import('./pages/EnhancedBusinessOwnerDashboard'))
const AnonymousLandingPage = lazy(() => import('./pages/AnonymousLandingPage'))
const AnonymousServiceBrowser = lazy(() => import('./pages/AnonymousServiceBrowser'))
const PublicBusinessListingsPage = lazy(() => import('./pages/PublicBusinessListingsPage'))
const BusinessDetailsPage = lazy(() => import('./pages/BusinessDetailsPage'))
const ProviderProfilePage = lazy(() => import('./pages/ProviderProfilePage'))
const AdoptionListingsPage = lazy(() => import('./pages/AdoptionListingsPage'))
const AdoptionDetailPage = lazy(() => import('./pages/AdoptionDetailPage'))
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'))
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const EnhancedSearchPage = lazy(() => import('./pages/EnhancedSearchPage'))
const EnhancedProfilePage = lazy(() => import('./pages/EnhancedProfilePage'))
const BookingPage = lazy(() => import('./pages/BookingPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const BusinessLoginPage = lazy(() => import('./pages/BusinessLoginPage'))
const BusinessRegisterPage = lazy(() => import('./pages/BusinessRegisterPage'))
const BusinessDashboardPage = lazy(() => import('./pages/BusinessDashboardPage'))
const BusinessesAdminPage = lazy(() => import('./pages/BusinessesAdminPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const AuthDemoPage = lazy(() => import('./pages/AuthDemoPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const AdminRoutes = lazy(() => import('./pages/AdminRoutes'))
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'))
const OrdersPage = lazy(() => import('./pages/OrdersPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))

function AppInner() {
  const { t } = useTranslation()
  // Enable automatic page tracking
  usePageTracking()
  const { consent } = useConsent()
  analyticsService.setConsent(!!consent.analytics)

  return (
    <AuthProvider>
      <AnonymousProvider>
        <ScrollToTop />
        <SkipLink href="#main" className="skip-link">
          {t('navigation.skipToMain')}
        </SkipLink>
        <Layout>
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/category/:category" element={<CategoryPage />} />
          <Route path="/services" element={<PublicBusinessListingsPage />} />
              <Route path="/businesses/:id" element={<BusinessDetailsPage />} />
              <Route path="/businesses/:id/book" element={<BookingPage />} />
              <Route path="/provider/:id" element={<ProviderProfilePage />} />
          <Route path="/adoption" element={<AdoptionListingsPage />} />
          <Route path="/adoption/:id" element={<AdoptionDetailPage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:slug" element={<ArticleDetailPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/search-advanced" element={<EnhancedSearchPage />} />
          <Route path="/browse" element={<AnonymousServiceBrowser />} />
          <Route path="/anonymous" element={<AnonymousLandingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/auth-demo" element={<AuthDemoPage />} />
          
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/business-register" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/business-login" element={<BusinessLoginPage />} />
          <Route path="/business-register" element={<BusinessRegisterPage />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="consumer">
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/profile-enhanced" element={
            <ProtectedRoute>
              <EnhancedProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute requiredRole="consumer">
              <FavoritesPage />
            </ProtectedRoute>
          } />
          <Route path="/bookings" element={
            <ProtectedRoute requiredRole="consumer">
              <OrdersPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/business-dashboard" element={
            <ProtectedRoute requiredRole="business">
              <BusinessDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/business-dashboard-enhanced" element={
            <ProtectedRoute requiredRole="business">
              <EnhancedBusinessOwnerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/business-profile" element={
            <ProtectedRoute requiredRole="business">
              <BusinessProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/business-listings" element={
            <ProtectedRoute requiredRoles={['business', 'admin']}>
              <BusinessListingsPage />
            </ProtectedRoute>
          } />
          <Route path="/business-listings/new" element={
            <ProtectedRoute requiredRole="business">
              <BusinessListingForm onSubmit={async () => {}} />
            </ProtectedRoute>
          } />
              <Route path="/admin-dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <EnhancedAdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/businesses" element={
                <ProtectedRoute requiredRole="admin">
                  <BusinessesAdminPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/businesses" element={
                <ProtectedRoute requiredRole="admin">
                  <BusinessesAdminPage />
                </ProtectedRoute>
              } />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute requiredRole="admin">
              <AdminRoutes />
            </ProtectedRoute>
          } />
          
          {/* 404 route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </Layout>
        <CookieConsent />
      </AnonymousProvider>
    </AuthProvider>
  )
}

export default function App() {
  return (
    <ConsentProvider>
      <AppInner />
    </ConsentProvider>
  )
}
